import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faCheck,
  faChevronDown,
  faChevronUp,
  faLightbulb,
  faPaperPlane,
  faTimes,
  faArrowRight,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

import { buildInitialSuggestionValue } from './dbqSuggestionUtils';
import EvidencePageViewer from './EvidencePageViewer';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildSuggestionStats = (suggestions = []) => suggestions.reduce((stats, suggestion) => {
  const status = String(suggestion?.status || 'pending').toLowerCase();

  stats.total += 1;
  if (status === 'accepted') stats.accepted += 1;
  else if (status === 'rejected') stats.rejected += 1;
  else if (status === 'dismissed') stats.dismissed += 1;
  else stats.pending += 1;

  return stats;
}, {
  total: 0,
  pending: 0,
  accepted: 0,
  rejected: 0,
  dismissed: 0,
});

const SUGGESTION_FILTER_LABELS = {
  pending: 'Open',
  accepted: 'Accepted',
  rejected: 'Rejected',
  dismissed: 'Dismissed',
};

const getSuggestionTextValue = (suggestion) =>
  String(suggestion?.payload?.value || suggestion?.summary || '').trim();

const isLongTextSuggestion = (suggestion, field) =>
  String(suggestion?.fieldType || field?.field_type || '').toLowerCase() === 'long_text';

const FULL_ANSWER_MIN_CHARACTERS = 500;
const FULL_ANSWER_MIN_WORDS = 80;

const isAiGeneratedSuggestion = (suggestion) =>
  suggestion?.source === 'ai_generated' || Boolean(suggestion?.aiDraft);

const isAiNoticeSuggestion = (suggestion) =>
  suggestion?.source === 'ai_notice' || Boolean(suggestion?.aiNotice);

const isMedicalOpinionDraftSuggestion = (suggestion) =>
  suggestion?.aiDraft?.draftType === 'medical_opinion';

const MISSING_USER_INPUT_TOKEN = '[MISSING – FLAG FOR USER]';

const getSuggestionMissingUserInputs = (suggestion) =>
  Array.isArray(suggestion?.aiDraft?.missingUserInputs) ? suggestion.aiDraft.missingUserInputs : [];

const suggestionHasMissingUserInput = (suggestion) =>
  getSuggestionTextValue(suggestion).includes(MISSING_USER_INPUT_TOKEN);

const isPrettyLongSuggestion = (suggestion) => {
  const text = getSuggestionTextValue(suggestion);
  if (text.length >= FULL_ANSWER_MIN_CHARACTERS) return true;
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  return wordCount >= FULL_ANSWER_MIN_WORDS;
};

const shouldUseFullAnswerGateway = ({
  suggestion,
  field,
  isProviderView = false,
} = {}) => {
  if (!isLongTextSuggestion(suggestion, field)) return false;
  if (!isProviderView) return true;
  return isPrettyLongSuggestion(suggestion);
};

const getSuggestionDisplayAuthor = (suggestion, isProviderView = false) => {
  if (isProviderView && (isAiGeneratedSuggestion(suggestion) || isAiNoticeSuggestion(suggestion))) {
    return 'Case Review';
  }

  return suggestion?.createdBy?.name || 'Unknown';
};

const formatDbqQuestionNumber = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return null;

  const normalizedValue = rawValue.replace(/[._-]+/g, '_');
  const idMatch =
    normalizedValue.match(/(?:^|_)q_(\d+[a-z])(?:_|$)/i) ||
    normalizedValue.match(/(?:^|_)sec\d+_(\d+[a-z])(?:_|$)/i) ||
    normalizedValue.match(/(?:^|_)(\d+[a-z])(?:_|$)/i);
  if (idMatch?.[1]) {
    return idMatch[1].toUpperCase();
  }

  const labelMatch = rawValue.match(/^\s*(\d+[a-z])(?:[).:\-\s]|$)/i);
  return labelMatch?.[1] ? labelMatch[1].toUpperCase() : null;
};

const getDbqQuestionNumber = ({ field = null, suggestion = null } = {}) => {
  const candidates = [
    field?.questionNumber,
    field?.question_number,
    field?.number,
    field?.id,
    suggestion?.fieldId,
    field?.label,
    suggestion?.fieldLabel,
  ];

  return candidates.map(formatDbqQuestionNumber).find(Boolean) || null;
};

const getDbqQuestionText = ({ field = null, fieldLabel = null, suggestion = null } = {}) =>
  String(
    field?.label ||
    fieldLabel ||
    suggestion?.fieldLabel ||
    'Question being answered',
  ).trim();

const SuggestionQuestionContext = ({ field, fieldLabel, suggestion }) => {
  const questionNumber = getDbqQuestionNumber({ field, suggestion });
  const questionText = getDbqQuestionText({ field, fieldLabel, suggestion });

  return (
    <div className="dbq-suggestion-question-context">
      {questionNumber ? (
        <span className="dbq-suggestion-question-number">{questionNumber}</span>
      ) : null}
      <span className="dbq-suggestion-question-text">{questionText}</span>
    </div>
  );
};

const buildMissingDetailEvidencePatch = (result = {}) => ({
  sourceEvidenceRefs: Array.isArray(result?.sourceEvidenceRefs) ? result.sourceEvidenceRefs : [],
  transcriptRefs: Array.isArray(result?.transcriptRefs) ? result.transcriptRefs : [],
});

const mergeMissingDetailEvidencePatch = (currentPatch = {}, nextPatch = {}) => ({
  sourceEvidenceRefs: [
    ...(Array.isArray(currentPatch?.sourceEvidenceRefs) ? currentPatch.sourceEvidenceRefs : []),
    ...(Array.isArray(nextPatch?.sourceEvidenceRefs) ? nextPatch.sourceEvidenceRefs : []),
  ],
  transcriptRefs: [
    ...(Array.isArray(currentPatch?.transcriptRefs) ? currentPatch.transcriptRefs : []),
    ...(Array.isArray(nextPatch?.transcriptRefs) ? nextPatch.transcriptRefs : []),
  ],
});

const hasMissingDetailEvidencePatch = (patch = {}) =>
  (Array.isArray(patch?.sourceEvidenceRefs) && patch.sourceEvidenceRefs.length > 0) ||
  (Array.isArray(patch?.transcriptRefs) && patch.transcriptRefs.length > 0);

const getSuggestionReviewAlertMessage = (suggestion) =>
  String(
    suggestion?.aiDraft?.reviewAlertMessage ||
    'This AI draft still contains missing information. Complete the missing detail before accepting it.',
  ).trim();

const getAiNoticeTone = (suggestion) =>
  String(suggestion?.aiNotice?.tone || 'info').trim().toLowerCase();

const getAiNoticeStatusText = (suggestion) => {
  const tone = getAiNoticeTone(suggestion);
  if (tone === 'warning') return 'Needs attention';
  if (tone === 'progress') return 'In progress';
  return 'Update';
};

const buildMissingInputStateKey = (missingInput, index) =>
  String(missingInput?.key || missingInput?.label || index);

const replaceNthOccurrence = (text, token, occurrenceIndex, replacement) => {
  const normalizedText = String(text || '');
  const normalizedToken = String(token || '');
  if (!normalizedToken) return normalizedText;
  if (!Number.isInteger(occurrenceIndex) || occurrenceIndex < 0) {
    return normalizedText.split(normalizedToken).join(replacement);
  }

  let nextText = '';
  let searchStart = 0;
  let currentOccurrence = 0;

  while (searchStart < normalizedText.length) {
    const tokenIndex = normalizedText.indexOf(normalizedToken, searchStart);
    if (tokenIndex === -1) {
      nextText += normalizedText.slice(searchStart);
      break;
    }

    nextText += normalizedText.slice(searchStart, tokenIndex);
    nextText += currentOccurrence === occurrenceIndex ? replacement : normalizedToken;
    searchStart = tokenIndex + normalizedToken.length;
    currentOccurrence += 1;
  }

  return nextText;
};

const formatSuggestionStatusText = (status) => {
  const normalizedStatus = String(status || '').toLowerCase();

  if (normalizedStatus === 'pending') return 'Pending review';
  if (normalizedStatus === 'accepted') return 'Accepted';
  if (normalizedStatus === 'rejected') return 'Rejected';
  if (normalizedStatus === 'dismissed') return 'Dismissed';
  return status || 'Pending review';
};

const AiNoticeCard = ({ suggestion, isProviderView = false }) => {
  const tone = getAiNoticeTone(suggestion);
  const title = String(
    isProviderView
      ? 'Suggestion update'
      : suggestion?.aiNotice?.title || suggestion?.summary || 'AI update',
  ).trim();
  const message = String(
    isProviderView
      ? 'This suggested answer is not available for this field.'
      : suggestion?.aiNotice?.message || suggestion?.payload?.value || suggestion?.summary || '',
  ).trim();

  return (
    <>
      <div className="dbq-suggestion-card-header">
        <div className="dbq-suggestion-card-meta">
          <span className={`dbq-suggestion-card-eyebrow dbq-suggestion-card-eyebrow--${tone}`}>
            <FontAwesomeIcon icon={faLightbulb} className="dbq-suggestion-card-eyebrow-icon" />
            {isProviderView ? 'Suggestion update' : 'AI update'}
          </span>
          <span className="dbq-suggestion-card-author">
            {getSuggestionDisplayAuthor(suggestion, isProviderView)}
          </span>
          <span className="dbq-suggestion-card-date">{formatDate(suggestion.createdAt)}</span>
        </div>
        <span className={`dbq-suggestion-card-status-text dbq-suggestion-card-status-text--notice-${tone}`}>
          {getAiNoticeStatusText(suggestion)}
        </span>
      </div>

      <div className="dbq-suggestion-card-body">
        <div className="dbq-suggestion-card-section-title">{title}</div>
        {message ? (
          <div className="dbq-suggestion-card-section-text">{message}</div>
        ) : null}
      </div>

      {suggestion.note ? (
        <div className="dbq-suggestion-card-note">
          <span className="dbq-suggestion-card-note-label">Details</span>
          {suggestion.note}
        </div>
      ) : null}
    </>
  );
};

/**
 * Renders the suggestion value in a structured way based on field type.
 * For multi_select: shows individual option pills/tags
 * For yes_no: shows Yes/No with an indicator
 * For other types: shows the formatted value
 */
const SuggestionValueDisplay = ({ suggestion, field }) => {
  const fieldType = suggestion.fieldType || field?.field_type || '';
  const value = suggestion.payload?.value;

  if (fieldType === 'multi_select' && Array.isArray(value)) {
    const options = field?.options || [];
    const labels = value.map((v) => {
      const opt = options.find((o) => o.id === v);
      return opt?.label || String(v);
    });
    return (
      <div className="dbq-suggestion-value-list">
        {labels.map((label, i) => (
          <span key={i} className="dbq-suggestion-value-tag">
            <span className="dbq-suggestion-value-check">&#10003;</span>
            {label}
          </span>
        ))}
      </div>
    );
  }

  if (fieldType === 'single_select') {
    const options = field?.options || [];
    const opt = options.find((o) => o.id === value);
    const label = opt?.label || String(value || '');
    return (
      <div className="dbq-suggestion-value-list">
        <span className="dbq-suggestion-value-tag dbq-suggestion-value-tag--single">
          <FontAwesomeIcon icon={faArrowRight} className="dbq-suggestion-value-arrow" />
          {label}
        </span>
      </div>
    );
  }

  if (fieldType === 'yes_no') {
    const label = value === true ? 'Yes' : value === false ? 'No' : String(value);
    return (
      <div className="dbq-suggestion-value-list">
        <span className={`dbq-suggestion-value-tag ${value === true ? 'dbq-suggestion-value-tag--yes' : 'dbq-suggestion-value-tag--no'}`}>
          {label}
        </span>
      </div>
    );
  }

  // Fallback: plain text
  return (
    <div className="dbq-suggestion-value-text">{suggestion.summary || String(value || 'No value')}</div>
  );
};

const SuggestionValueInput = ({ field, value, onChange }) => {
  const fieldType = String(field?.field_type || '').toLowerCase();

  if (fieldType === 'yes_no') {
    return (
      <div className="d-flex gap-3">
        <Form.Check
          type="radio"
          id="suggestion-yes"
          name="suggestion-yes-no"
          label="Yes"
          checked={value === true}
          onChange={() => onChange(true)}
        />
        <Form.Check
          type="radio"
          id="suggestion-no"
          name="suggestion-yes-no"
          label="No"
          checked={value === false}
          onChange={() => onChange(false)}
        />
      </div>
    );
  }

  if (fieldType === 'checkbox') {
    return (
      <Form.Check
        type="checkbox"
        id="suggestion-checkbox"
        label="Checked"
        checked={Boolean(value)}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  }

  if (fieldType === 'single_select') {
    return (
      <Form.Select value={value || ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">-- Select option --</option>
        {(field?.options || []).map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (fieldType === 'multi_select') {
    const selectedValues = Array.isArray(value) ? value : [];
    return (
      <div className="d-flex flex-column gap-2">
        {(field?.options || []).map((option) => {
          const isChecked = selectedValues.includes(option.id);
          return (
            <Form.Check
              key={option.id}
              type="checkbox"
              id={`suggestion-${option.id}`}
              label={option.label}
              checked={isChecked}
              onChange={() => {
                const nextValue = isChecked
                  ? selectedValues.filter((item) => item !== option.id)
                  : [...selectedValues, option.id];
                onChange(nextValue);
              }}
            />
          );
        })}
      </div>
    );
  }

  if (fieldType === 'long_text') {
    return (
      <Form.Control
        as="textarea"
        rows={4}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Suggested answer"
      />
    );
  }

  if (fieldType === 'date') {
    return (
      <Form.Control
        type="date"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'number') {
    return (
      <Form.Control
        type="number"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Suggested answer"
      />
    );
  }

  return (
    <Form.Control
      type="text"
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Suggested answer"
    />
  );
};

const getSuggestionEvidenceRefs = (suggestion) =>
  (Array.isArray(suggestion?.aiDraft?.sourceEvidenceRefs) ? suggestion.aiDraft.sourceEvidenceRefs : [])
    .map((ref) => {
      const pages = (Array.isArray(ref?.topPages) ? ref.topPages : [])
        .map((page) => Number(page?.pageNumber))
        .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber > 0);

      return {
        fileId: ref?.fileId || null,
        fileName: String(ref?.fileName || '').trim() || 'Evidence file',
        pages,
      };
    })
    .filter((ref) => ref.pages.length > 0);

const getInitialSuggestionEvidencePage = (suggestion) => {
  const firstRef = getSuggestionEvidenceRefs(suggestion)[0];
  const firstPageNumber = firstRef?.pages?.[0];

  if (!firstRef?.fileId || !firstPageNumber) {
    return {
      fileId: null,
      fileName: null,
      pageNumber: null,
    };
  }

  return {
    fileId: firstRef.fileId,
    fileName: firstRef.fileName,
    pageNumber: firstPageNumber,
  };
};

const DbqSuggestionsPanel = ({
  suggestions = [],
  field,
  fieldId,
  fieldLabel,
  claimId = null,
  currentValue,
  canSuggest = false,
  canAcceptSuggestions = false,
  canDismissSuggestions = false,
  canEditMedicalOpinionDraftSuggestions = false,
  onAddSuggestion,
  onAcceptSuggestion,
  onSaveSuggestionDraft,
  onRejectSuggestion,
  onDismissSuggestion,
  onSearchMissingDetail,
  onNavigate,
  suggestionFieldIds = [],
  suggestionStats = null,
  activeStatusFilter = 'pending',
  onStatusFilterChange,
  onClose,
  isSubmitting = false,
  embedded = false,
  showStatusFilter = true,
  statusSummaryText = null,
  isProviderView = false,
  openNewSuggestionComposer = false,
}) => {
  const useProviderSuggestionView =
    Boolean(isProviderView) ||
    (!canSuggest && canAcceptSuggestions && !canDismissSuggestions);
  const [showNewSuggestion, setShowNewSuggestion] = useState(false);
  const [suggestionValue, setSuggestionValue] = useState(buildInitialSuggestionValue(field, currentValue));
  const [suggestionNote, setSuggestionNote] = useState('');
  const [fullAnswerSuggestion, setFullAnswerSuggestion] = useState(null);
  const [fullAnswerValue, setFullAnswerValue] = useState('');
  const [missingInputValues, setMissingInputValues] = useState({});
  const [missingInputSearches, setMissingInputSearches] = useState({});
  const [fullAnswerEvidencePatch, setFullAnswerEvidencePatch] = useState({
    sourceEvidenceRefs: [],
    transcriptRefs: [],
  });
  const [isFullAnswerSubmitting, setIsFullAnswerSubmitting] = useState(false);
  const [evidenceSuggestion, setEvidenceSuggestion] = useState(null);
  const [selectedEvidencePage, setSelectedEvidencePage] = useState({
    fileId: null,
    fileName: null,
    pageNumber: null,
  });

  useEffect(() => {
    setShowNewSuggestion(false);
    setSuggestionValue(buildInitialSuggestionValue(field, currentValue));
    setSuggestionNote('');
  }, [field, currentValue, fieldId]);

  useEffect(() => {
    setFullAnswerSuggestion(null);
    setFullAnswerValue('');
    setMissingInputValues({});
    setMissingInputSearches({});
    setFullAnswerEvidencePatch({
      sourceEvidenceRefs: [],
      transcriptRefs: [],
    });
    setIsFullAnswerSubmitting(false);
    setEvidenceSuggestion(null);
    setSelectedEvidencePage({
      fileId: null,
      fileName: null,
      pageNumber: null,
    });
  }, [fieldId]);

  const currentIndex = suggestionFieldIds.indexOf(fieldId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < suggestionFieldIds.length - 1 && currentIndex !== -1;
  const displayableSuggestions = useMemo(
    () => (
      useProviderSuggestionView
        ? suggestions.filter((suggestion) => !isAiNoticeSuggestion(suggestion))
        : suggestions
    ),
    [suggestions, useProviderSuggestionView],
  );
  const hasPendingEditableAiDraftSuggestion = useMemo(
    () => displayableSuggestions.some((suggestion) =>
      suggestion?.status === 'pending' &&
      isAiGeneratedSuggestion(suggestion) &&
      isLongTextSuggestion(suggestion, field)),
    [displayableSuggestions, field],
  );
  const canComposeManualSuggestion = canSuggest && !hasPendingEditableAiDraftSuggestion;
  useEffect(() => {
    if (!canComposeManualSuggestion || !openNewSuggestionComposer) return;
    setShowNewSuggestion(true);
    setSuggestionValue(buildInitialSuggestionValue(field, currentValue));
    setSuggestionNote('');
  }, [canComposeManualSuggestion, currentValue, field, openNewSuggestionComposer]);

  useEffect(() => {
    if (canComposeManualSuggestion || !showNewSuggestion) return;
    setShowNewSuggestion(false);
  }, [canComposeManualSuggestion, showNewSuggestion]);
  const resolvedSuggestionStats = useMemo(
    () => (
      suggestionStats && !useProviderSuggestionView
        ? suggestionStats
        : buildSuggestionStats(displayableSuggestions)
    ),
    [displayableSuggestions, suggestionStats, useProviderSuggestionView],
  );
  const filterOptions = useMemo(() => ([
    { key: 'pending', count: resolvedSuggestionStats.pending },
    { key: 'accepted', count: resolvedSuggestionStats.accepted },
    { key: 'rejected', count: resolvedSuggestionStats.rejected },
    { key: 'dismissed', count: resolvedSuggestionStats.dismissed },
  ].filter((option) => option.count > 0)), [resolvedSuggestionStats]);
  const effectiveStatusFilter = filterOptions.some((option) => option.key === activeStatusFilter)
    ? activeStatusFilter
    : (filterOptions[0]?.key || activeStatusFilter);
  const visibleSuggestions = useMemo(() => {
    return displayableSuggestions.filter(
      (suggestion) => String(suggestion?.status || '').toLowerCase() === effectiveStatusFilter,
    );
  }, [displayableSuggestions, effectiveStatusFilter]);

  const openEvidenceModal = (suggestion) => {
    setEvidenceSuggestion(suggestion);
    setSelectedEvidencePage(getInitialSuggestionEvidencePage(suggestion));
  };

  const closeEvidenceModal = () => {
    setEvidenceSuggestion(null);
    setSelectedEvidencePage({
      fileId: null,
      fileName: null,
      pageNumber: null,
    });
  };

  const openEvidencePage = ({ fileId, fileName, pageNumber }) => {
    if (!claimId || !fileId || !pageNumber) return;
    setSelectedEvidencePage({
      fileId,
      fileName: fileName || null,
      pageNumber,
    });
  };

  const evidenceRefs = useMemo(
    () => getSuggestionEvidenceRefs(evidenceSuggestion),
    [evidenceSuggestion],
  );

  const openFullAnswerModal = (suggestion) => {
    setFullAnswerSuggestion(suggestion);
    setFullAnswerValue(getSuggestionTextValue(suggestion));
    setMissingInputValues(
      Object.fromEntries(
        getSuggestionMissingUserInputs(suggestion).map((missingInput, index) => [
          buildMissingInputStateKey(missingInput, index),
          '',
        ]),
      ),
    );
    setMissingInputSearches({});
    setFullAnswerEvidencePatch({
      sourceEvidenceRefs: [],
      transcriptRefs: [],
    });
  };

  const closeFullAnswerModal = (force = false) => {
    if (isFullAnswerSubmitting && !force) return;
    setFullAnswerSuggestion(null);
    setFullAnswerValue('');
    setMissingInputValues({});
    setMissingInputSearches({});
    setFullAnswerEvidencePatch({
      sourceEvidenceRefs: [],
      transcriptRefs: [],
    });
  };

  const fullAnswerMissingUserInputs = useMemo(
    () => getSuggestionMissingUserInputs(fullAnswerSuggestion),
    [fullAnswerSuggestion],
  );
  const fullAnswerHasMissingUserInput = useMemo(
    () => String(fullAnswerValue || '').includes(MISSING_USER_INPUT_TOKEN),
    [fullAnswerValue],
  );
  const canEditFullAnswerSuggestion = Boolean(
    fullAnswerSuggestion &&
    isAiGeneratedSuggestion(fullAnswerSuggestion) &&
    isLongTextSuggestion(fullAnswerSuggestion, field) &&
    fullAnswerSuggestion?.status === 'pending' &&
    (
      canAcceptSuggestions ||
      (
        canEditMedicalOpinionDraftSuggestions &&
        isMedicalOpinionDraftSuggestion(fullAnswerSuggestion) &&
        typeof onSaveSuggestionDraft === 'function'
      )
    ),
  );
  const canSaveFullAnswerSuggestionDraft = Boolean(
    fullAnswerSuggestion &&
    fullAnswerSuggestion?.status === 'pending' &&
    !canAcceptSuggestions &&
    canEditMedicalOpinionDraftSuggestions &&
    isMedicalOpinionDraftSuggestion(fullAnswerSuggestion) &&
    typeof onSaveSuggestionDraft === 'function',
  );
  const fullAnswerReviewAlertMessage = fullAnswerSuggestion
    ? getSuggestionReviewAlertMessage(fullAnswerSuggestion)
    : '';
  const fullAnswerModalTitle = fullAnswerSuggestion
    ? useProviderSuggestionView
      ? 'View Full Suggested Answer'
      : fullAnswerSuggestion?.aiDraft?.draftType === 'medical_opinion'
        ? 'Review Full Medical Opinion Draft'
        : (suggestionHasMissingUserInput(fullAnswerSuggestion)
          ? 'Complete Suggested Answer'
          : 'View Full Suggested Answer')
    : 'View Full Suggested Answer';
  const fullAnswerAcceptLabel = useProviderSuggestionView ? 'Accept Suggestion' : 'Accept Draft';
  const fullAnswerEditHelpText = canSaveFullAnswerSuggestionDraft
    ? 'Edits in this window update the pending Medical Opinion draft. Closing this window discards unsaved changes.'
    : (useProviderSuggestionView
      ? 'Edits in this window are temporary until you accept the suggestion. Closing this window discards them.'
      : 'Edits in this window are temporary until you accept the draft. Closing this window discards them.');

  const handleMissingInputValueChange = (missingInput, index, nextValue) => {
    const stateKey = buildMissingInputStateKey(missingInput, index);
    setMissingInputValues((currentValues) => ({
      ...currentValues,
      [stateKey]: nextValue,
    }));
  };

  const handleApplyMissingInputValue = (missingInput, index) => {
    const stateKey = buildMissingInputStateKey(missingInput, index);
    const replacementValue = String(missingInputValues?.[stateKey] || '').trim();
    const searchState = missingInputSearches?.[stateKey] || null;
    const token = String(missingInput?.token || MISSING_USER_INPUT_TOKEN).trim();
    if (!replacementValue || !token) return;

    setFullAnswerValue((currentValue) => replaceNthOccurrence(
      currentValue,
      token,
      Number.isInteger(missingInput?.occurrenceIndex) ? missingInput.occurrenceIndex : null,
      replacementValue,
    ));
    if (searchState?.status === 'found') {
      const evidencePatch = buildMissingDetailEvidencePatch(searchState);
      if (hasMissingDetailEvidencePatch(evidencePatch)) {
        setFullAnswerEvidencePatch((currentPatch) => mergeMissingDetailEvidencePatch(
          currentPatch,
          evidencePatch,
        ));
      }
    }
  };

  const handleSearchMissingInputValue = async (missingInput, index) => {
    if (!fullAnswerSuggestion || !onSearchMissingDetail) return;

    const stateKey = buildMissingInputStateKey(missingInput, index);
    setMissingInputSearches((currentSearches) => ({
      ...currentSearches,
      [stateKey]: {
        status: 'searching',
        message: 'Searching selected evidence and transcripts...',
      },
    }));

    try {
      const result = await onSearchMissingDetail({
        suggestionId: fullAnswerSuggestion.suggestionId,
        suggestion: fullAnswerSuggestion,
        missingInput,
        missingInputIndex: index,
        currentDraftText: fullAnswerValue,
      });
      const foundValue = String(result?.value || result?.answer || '').trim();

      if (result?.found && foundValue) {
        setMissingInputValues((currentValues) => ({
          ...currentValues,
          [stateKey]: foundValue,
        }));
        setMissingInputSearches((currentSearches) => ({
          ...currentSearches,
          [stateKey]: {
            status: 'found',
            message: result?.explanation || 'AI found a supported value. Review it, then insert it if correct.',
            ...buildMissingDetailEvidencePatch(result),
          },
        }));
        return;
      }

      setMissingInputSearches((currentSearches) => ({
        ...currentSearches,
        [stateKey]: {
          status: 'not_found',
          message:
            result?.explanation ||
            'AI could not find this specific detail in the selected evidence or transcripts.',
        },
      }));
    } catch (error) {
      setMissingInputSearches((currentSearches) => ({
        ...currentSearches,
        [stateKey]: {
          status: 'error',
          message:
            error?.response?.data?.msg ||
            error?.message ||
            'Failed to search selected evidence and transcripts.',
        },
      }));
    }
  };

  const renderMissingInputCallout = () => {
    if (useProviderSuggestionView) return null;
    if (!fullAnswerHasMissingUserInput) return null;

    return (
      <div className="dbq-suggestion-missing-callout">
        <div className="dbq-suggestion-missing-callout-title">Complete missing detail</div>
        <div className="dbq-suggestion-missing-callout-text">
          {fullAnswerReviewAlertMessage}
        </div>
        {fullAnswerMissingUserInputs.length > 0 ? (
          <div className="dbq-suggestion-missing-fields">
            {fullAnswerMissingUserInputs.map((missingInput, index) => {
              const stateKey = buildMissingInputStateKey(missingInput, index);
              const inputValue = missingInputValues?.[stateKey] || '';
              const searchState = missingInputSearches?.[stateKey] || null;
              const isSearching = searchState?.status === 'searching';
              return (
                <div key={stateKey} className="dbq-suggestion-missing-field">
                  <label className="dbq-suggestion-missing-field-label">
                    {missingInput?.label || 'Missing detail'}
                  </label>
                  {missingInput?.contextPreview ? (
                    <div className="dbq-suggestion-missing-field-context">
                      {missingInput.contextPreview}
                    </div>
                  ) : null}
                  {missingInput?.helpText ? (
                    <div className="dbq-suggestion-missing-field-help">{missingInput.helpText}</div>
                  ) : null}
                  {onSearchMissingDetail ? (
                    <div className="dbq-suggestion-missing-search-row">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleSearchMissingInputValue(missingInput, index)}
                        disabled={isFullAnswerSubmitting || isSearching}
                      >
                        <FontAwesomeIcon icon={faSearch} className="me-1" />
                        {isSearching ? 'Searching...' : 'Search evidence'}
                      </Button>
                      {searchState?.message ? (
                        <div className={`dbq-suggestion-missing-search-message dbq-suggestion-missing-search-message--${searchState.status}`}>
                          {searchState.message}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="dbq-suggestion-missing-field-actions">
                    <Form.Control
                      type="text"
                      value={inputValue}
                      onChange={(event) => handleMissingInputValueChange(missingInput, index, event.target.value)}
                      placeholder={missingInput?.placeholder || 'Enter missing detail'}
                      disabled={isFullAnswerSubmitting}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => handleApplyMissingInputValue(missingInput, index)}
                      disabled={isFullAnswerSubmitting || !String(inputValue || '').trim()}
                    >
                      Insert
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const handleAcceptFullAnswer = async () => {
    if (!fullAnswerSuggestion || !onAcceptSuggestion) return;

    setIsFullAnswerSubmitting(true);
    try {
      await onAcceptSuggestion(fullAnswerSuggestion.suggestionId, {
        editedValue: fullAnswerValue,
        suggestion: fullAnswerSuggestion,
        ...(hasMissingDetailEvidencePatch(fullAnswerEvidencePatch)
          ? fullAnswerEvidencePatch
          : {}),
      });
      closeFullAnswerModal(true);
    } catch (error) {
      // Parent mutation handlers surface their own errors.
    } finally {
      setIsFullAnswerSubmitting(false);
    }
  };

  const handleDismissFullAnswer = async () => {
    if (!fullAnswerSuggestion || !onDismissSuggestion) return;

    setIsFullAnswerSubmitting(true);
    try {
      await onDismissSuggestion(fullAnswerSuggestion.suggestionId);
      closeFullAnswerModal(true);
    } catch (error) {
      // Parent mutation handlers surface their own errors.
    } finally {
      setIsFullAnswerSubmitting(false);
    }
  };

  const handleSaveFullAnswerDraft = async () => {
    if (!fullAnswerSuggestion || !onSaveSuggestionDraft) return;

    setIsFullAnswerSubmitting(true);
    try {
      await onSaveSuggestionDraft(fullAnswerSuggestion.suggestionId, {
        editedValue: fullAnswerValue,
        suggestion: fullAnswerSuggestion,
        ...(hasMissingDetailEvidencePatch(fullAnswerEvidencePatch)
          ? fullAnswerEvidencePatch
          : {}),
      });
      closeFullAnswerModal(true);
    } catch (error) {
      // Parent mutation handlers surface their own errors.
    } finally {
      setIsFullAnswerSubmitting(false);
    }
  };

  const handleNavigate = (direction) => {
    if (direction === 'up' && hasPrev) {
      onNavigate?.(suggestionFieldIds[currentIndex - 1]);
    } else if (direction === 'down' && hasNext) {
      onNavigate?.(suggestionFieldIds[currentIndex + 1]);
    }
  };

  const handleSubmit = () => {
    if (!onAddSuggestion) return;
    onAddSuggestion(fieldId, suggestionValue, suggestionNote);
    setShowNewSuggestion(false);
    setSuggestionValue(buildInitialSuggestionValue(field, currentValue));
    setSuggestionNote('');
  };

  const content = (
    <>
      {statusSummaryText ? (
        <div className="small text-muted mt-4 mb-2">{statusSummaryText}</div>
      ) : null}
      {showStatusFilter && resolvedSuggestionStats.total > 0 && filterOptions.length > 0 && (
        <div className="mt-4 mb-3" aria-label="Suggestion filter">
          <Form.Select
            size="sm"
            value={effectiveStatusFilter}
            onChange={(event) => onStatusFilterChange?.(event.target.value)}
            className="dbq-suggestion-filter-select"
          >
            {filterOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {SUGGESTION_FILTER_LABELS[option.key]} ({option.count})
              </option>
            ))}
          </Form.Select>
        </div>
      )}
      <div className="dbq-comments-list">
        {displayableSuggestions.length === 0 ? (
          <div className="dbq-no-comments">
            <p>No suggestions yet</p>
            {canComposeManualSuggestion && !showNewSuggestion && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowNewSuggestion(true)}
              >
                Add first suggestion
              </Button>
            )}
          </div>
        ) : visibleSuggestions.length === 0 ? (
          <div className="dbq-no-comments">
            <p>No {SUGGESTION_FILTER_LABELS[effectiveStatusFilter]?.toLowerCase() || ''} suggestions on this field.</p>
          </div>
        ) : (
          visibleSuggestions.map((suggestion) => {
            const showInlineAiLongAnswer =
              useProviderSuggestionView &&
              isAiGeneratedSuggestion(suggestion) &&
              isLongTextSuggestion(suggestion, field);
            const useFullAnswerGateway = !showInlineAiLongAnswer && shouldUseFullAnswerGateway({
              suggestion,
              field,
              isProviderView: useProviderSuggestionView,
            });
            const showMissingInputPrompt =
              !useProviderSuggestionView && suggestionHasMissingUserInput(suggestion);
            const hasEvidencePages = getSuggestionEvidenceRefs(suggestion).length > 0;

            return (
            <div
              key={suggestion.suggestionId}
              className={`dbq-suggestion-card dbq-suggestion-card--${suggestion.status}${isAiNoticeSuggestion(suggestion) ? ` dbq-suggestion-card--notice-${getAiNoticeTone(suggestion)}` : ''}`}
            >
              {isAiNoticeSuggestion(suggestion) ? (
                <AiNoticeCard suggestion={suggestion} isProviderView={useProviderSuggestionView} />
              ) : (
                <>
                  <div className="dbq-suggestion-card-header">
                    <div className="dbq-suggestion-card-meta">
                      <span className="dbq-suggestion-card-author">
                        {getSuggestionDisplayAuthor(suggestion, useProviderSuggestionView)}
                      </span>
                      <span className="dbq-suggestion-card-date">{formatDate(suggestion.createdAt)}</span>
                    </div>
                    <span className={`dbq-suggestion-card-status-text dbq-suggestion-card-status-text--${suggestion.status}`}>
                      {formatSuggestionStatusText(suggestion.status)}
                    </span>
                  </div>

                  <div className="dbq-suggestion-card-body">
                    {useFullAnswerGateway ? (
                      <div className="dbq-suggestion-answer-gateway">
                        <SuggestionQuestionContext
                          field={field}
                          fieldLabel={fieldLabel}
                          suggestion={suggestion}
                        />
                        {showMissingInputPrompt ? (
                          <div className="dbq-suggestion-missing-inline">
                            <span className="dbq-suggestion-missing-inline-label">Needs case manager input</span>
                            <span className="dbq-suggestion-missing-inline-text">
                              {getSuggestionReviewAlertMessage(suggestion)}
                            </span>
                          </div>
                        ) : null}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="dbq-suggestion-answer-btn"
                          onClick={() => openFullAnswerModal(suggestion)}
                        >
                          {showMissingInputPrompt ? 'Complete missing detail' : 'View full answer'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <SuggestionQuestionContext
                          field={field}
                          fieldLabel={fieldLabel}
                          suggestion={suggestion}
                        />
                        <SuggestionValueDisplay suggestion={suggestion} field={field} />
                      </>
                    )}
                    {isAiGeneratedSuggestion(suggestion) && hasEvidencePages ? (
                      <div className="mt-3">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openEvidenceModal(suggestion)}
                        >
                          View Evidence
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  {suggestion.note && (
                    <div className="dbq-suggestion-card-note">
                      <span className="dbq-suggestion-card-note-label">Note</span>
                      {suggestion.note}
                    </div>
                  )}

                  {suggestion.decisionNote && (
                    <div className="dbq-suggestion-card-note dbq-suggestion-card-decision-note">
                      <span className="dbq-suggestion-card-note-label">Decision</span>
                      {suggestion.decisionNote}
                    </div>
                  )}

                  {suggestion.status === 'pending' && (
                    <div className="dbq-suggestion-card-actions">
                      {canAcceptSuggestions && (
                        <>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="dbq-suggestion-action-btn"
                            onClick={() => onAcceptSuggestion?.(suggestion.suggestionId, { suggestion })}
                            disabled={isSubmitting}
                          >
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            Accept
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="dbq-suggestion-action-btn"
                            onClick={() => onRejectSuggestion?.(suggestion.suggestionId)}
                            disabled={isSubmitting}
                          >
                            <FontAwesomeIcon icon={faBan} className="me-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {canDismissSuggestions && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="dbq-suggestion-action-btn ms-auto"
                          onClick={() => onDismissSuggestion?.(suggestion.suggestionId)}
                          disabled={isSubmitting}
                        >
                          Dismiss
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            );
          })
        )}
      </div>

      {canComposeManualSuggestion && displayableSuggestions.length > 0 && !showNewSuggestion && (
        <div className="dbq-add-comment-trigger">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowNewSuggestion(true)}
          >
            <FontAwesomeIcon icon={faLightbulb} className="me-1" />
            Add suggestion
          </Button>
        </div>
      )}

      {canComposeManualSuggestion && showNewSuggestion && (
        <div className="dbq-new-comment animate-fade-in">
          <div className="small text-muted mb-2">Suggested answer</div>
          <div className="dbq-suggestion-input-scroll" data-testid="dbq-suggestion-input-scroll">
            <SuggestionValueInput
              field={field}
              value={suggestionValue}
              onChange={setSuggestionValue}
            />
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            value={suggestionNote}
            onChange={(event) => setSuggestionNote(event.target.value)}
            placeholder="Optional note for the provider"
            className="dbq-new-comment-textarea mt-3"
          />
          <div className="dbq-new-comment-actions">
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setShowNewSuggestion(false);
                setSuggestionValue(buildInitialSuggestionValue(field, currentValue));
                setSuggestionNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
              Post
            </Button>
          </div>
        </div>
      )}
    </>
  );

  const evidenceModal = (
    <Modal show={Boolean(evidenceSuggestion)} onHide={closeEvidenceModal} fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>Evidence Used</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: 'calc(100vh - 132px)', padding: '1rem 1.25rem' }}>
        <div className="small text-muted mb-3">
          Page-level evidence used for{' '}
          <strong>
            {evidenceSuggestion
              ? getDbqQuestionText({ field, fieldLabel, suggestion: evidenceSuggestion })
              : 'this suggestion'}
          </strong>
          .
        </div>

        {evidenceRefs.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '260px minmax(0, 1fr)',
              gap: '1rem',
              height: 'calc(100% - 28px)',
              minHeight: 0,
            }}
          >
            <div
              style={{
                minHeight: 0,
                overflowY: 'auto',
                borderRight: '1px solid rgba(0,0,0,0.08)',
                paddingRight: '1rem',
              }}
            >
              <div className="dbq-suggestion-provenance-section">
                <span className="dbq-suggestion-provenance-label">Evidence pages</span>
                <div className="dbq-suggestion-evidence-list">
                  {evidenceRefs.map((ref) => (
                    <div
                      key={`${ref.fileId || ref.fileName}`}
                      className="dbq-suggestion-evidence-item"
                    >
                      <div className="dbq-suggestion-evidence-file dbq-suggestion-evidence-file--static">
                        {ref.fileName}
                      </div>
                      <div className="dbq-suggestion-evidence-pages">
                        {ref.pages.map((pageNumber) => (
                          <button
                            key={`${ref.fileId || ref.fileName}-${pageNumber}`}
                            type="button"
                            className="dbq-suggestion-evidence-page"
                            aria-pressed={
                              selectedEvidencePage.fileId === ref.fileId &&
                              selectedEvidencePage.pageNumber === pageNumber
                            }
                            onClick={() => openEvidencePage({
                              fileId: ref.fileId,
                              fileName: ref.fileName,
                              pageNumber,
                            })}
                          >
                            {`p.${pageNumber}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ minWidth: 0, minHeight: 0, height: '100%' }}>
              {selectedEvidencePage.fileId && selectedEvidencePage.pageNumber ? (
                <EvidencePageViewer
                  claimId={claimId}
                  fileId={selectedEvidencePage.fileId}
                  fileName={selectedEvidencePage.fileName}
                  pageNumber={selectedEvidencePage.pageNumber}
                />
              ) : (
                <div className="alert alert-light mb-0">
                  Select an evidence page to view the original document.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="alert alert-warning mb-0">
            No page-level evidence was captured for this AI suggestion.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeEvidenceModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (embedded) {
    const fullAnswerModal = (
      <Modal show={Boolean(fullAnswerSuggestion)} onHide={closeFullAnswerModal} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{fullAnswerModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {canEditFullAnswerSuggestion ? (
            <>
              <div className="small text-muted mb-3">
                {fullAnswerEditHelpText}
              </div>
              {renderMissingInputCallout()}
              <Form.Control
                as="textarea"
                rows={16}
                value={fullAnswerValue}
                onChange={(event) => setFullAnswerValue(event.target.value)}
                disabled={isFullAnswerSubmitting}
              />
            </>
          ) : (
            <Form.Control
              as="textarea"
              rows={16}
              value={fullAnswerValue}
              readOnly
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeFullAnswerModal}
            disabled={isFullAnswerSubmitting}
          >
            Close
          </Button>
          {fullAnswerSuggestion?.status === 'pending' && canDismissSuggestions && onDismissSuggestion ? (
            <Button
              variant="outline-danger"
              onClick={handleDismissFullAnswer}
              disabled={isFullAnswerSubmitting}
            >
              Dismiss
            </Button>
          ) : null}
          {canSaveFullAnswerSuggestionDraft ? (
            <Button
              variant="primary"
              onClick={handleSaveFullAnswerDraft}
              disabled={
                isFullAnswerSubmitting ||
                !String(fullAnswerValue || '').trim() ||
                fullAnswerHasMissingUserInput
              }
            >
              Save Draft
            </Button>
          ) : null}
          {canEditFullAnswerSuggestion && !canSaveFullAnswerSuggestionDraft ? (
            <Button
              variant="success"
              onClick={handleAcceptFullAnswer}
              disabled={
                isFullAnswerSubmitting ||
                !String(fullAnswerValue || '').trim() ||
                fullAnswerHasMissingUserInput
              }
            >
              {fullAnswerAcceptLabel}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    );

    return (
      <>
        {content}
        {fullAnswerModal}
        {evidenceModal}
      </>
    );
  }

  return (
    <>
      <div className="dbq-comments-panel dbq-suggestions-panel">
        <div className="dbq-comments-header">
          <div className="dbq-comments-title">
            <h4>Suggestions</h4>
            <span className="dbq-comments-field-label">{fieldLabel}</span>
          </div>
          <div className="dbq-comments-nav">
            <Button
              variant="link"
              className="dbq-nav-btn"
              onClick={() => handleNavigate('up')}
              disabled={!hasPrev}
              title="Previous suggestion"
            >
              <FontAwesomeIcon icon={faChevronUp} />
            </Button>
            <Button
              variant="link"
              className="dbq-nav-btn"
              onClick={() => handleNavigate('down')}
              disabled={!hasNext}
              title="Next suggestion"
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </Button>
            <Button
              variant="link"
              className="dbq-close-btn"
              onClick={onClose}
              title="Close suggestions"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        </div>
        {content}
      </div>

      <Modal show={Boolean(fullAnswerSuggestion)} onHide={closeFullAnswerModal} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{fullAnswerModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {canEditFullAnswerSuggestion ? (
            <>
              <div className="small text-muted mb-3">
                {fullAnswerEditHelpText}
              </div>
              {renderMissingInputCallout()}
              <Form.Control
                as="textarea"
                rows={16}
                value={fullAnswerValue}
                onChange={(event) => setFullAnswerValue(event.target.value)}
                disabled={isFullAnswerSubmitting}
              />
            </>
          ) : (
            <Form.Control
              as="textarea"
              rows={16}
              value={fullAnswerValue}
              readOnly
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeFullAnswerModal}
            disabled={isFullAnswerSubmitting}
          >
            Close
          </Button>
          {fullAnswerSuggestion?.status === 'pending' && canDismissSuggestions && onDismissSuggestion ? (
            <Button
              variant="outline-danger"
              onClick={handleDismissFullAnswer}
              disabled={isFullAnswerSubmitting}
            >
              Dismiss
            </Button>
          ) : null}
          {canSaveFullAnswerSuggestionDraft ? (
            <Button
              variant="primary"
              onClick={handleSaveFullAnswerDraft}
              disabled={
                isFullAnswerSubmitting ||
                !String(fullAnswerValue || '').trim() ||
                fullAnswerHasMissingUserInput
              }
            >
              Save Draft
            </Button>
          ) : null}
          {canEditFullAnswerSuggestion && !canSaveFullAnswerSuggestionDraft ? (
            <Button
              variant="success"
              onClick={handleAcceptFullAnswer}
              disabled={
                isFullAnswerSubmitting ||
                !String(fullAnswerValue || '').trim() ||
                fullAnswerHasMissingUserInput
              }
            >
              {fullAnswerAcceptLabel}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
      {evidenceModal}
    </>
  );
};

export default DbqSuggestionsPanel;
